import {
  Scene,
  Vector3,
  MeshBuilder,
  Mesh,
  TransformNode,
  StandardMaterial,
  Color3,
} from "@babylonjs/core";
import { getSharedMaterials } from "./materials.js";
import { PropLibrary } from "./propLibrary.js";
import { DynamicTextureSurface } from "./surfaces/DynamicTextureSurface.js";

/**
 * Shared detailed prop builders. Every prop is built from curved primitives
 * (lathes, high-tessellation cylinders, tori) instead of raw boxes so the
 * mall reads as finished rather than blocky. Only load-bearing pieces get
 * checkCollisions; small trim stays non-collidable to keep the physics
 * broadphase lean. All parts parent to `parent`, so zone-level
 * mergeStaticChildren still folds them into a few draw calls.
 */

/** Soft dark radial decal just above the floor: fake ambient-occlusion
 *  contact shadow. Costs one alpha-blended quad (and zones merge them into a
 *  single mesh, since they all share one material), works on every tier, and
 *  is the difference between "toy placed on floor" and "object standing on
 *  floor". radiusZ defaults to radiusX (circular); pass both for elongated
 *  props like benches. */
export function addContactShadow(
  scene: Scene,
  parent: TransformNode,
  name: string,
  position: Vector3,
  radiusX: number,
  radiusZ: number = radiusX
): Mesh {
  const mats = getSharedMaterials(scene);
  const decal: Mesh = MeshBuilder.CreateGround(
    `${name}-shadow`, { width: radiusX * 2, height: radiusZ * 2 }, scene
  );
  decal.position = new Vector3(position.x, position.y + 0.015, position.z);
  decal.material = mats.contactShadow;
  decal.isPickable = false;
  decal.parent = parent;
  return decal;
}

/** Park-style bench: slatted seat + backrest on curved steel legs. */
export function buildBench(
  scene: Scene,
  parent: TransformNode,
  name: string,
  pos: Vector3,
  rotY: number
): void {
  const mats = getSharedMaterials(scene);
  const node: TransformNode = new TransformNode(name, scene);
  node.parent = parent;
  node.position = pos.clone();
  node.rotation.y = rotY;

  // Seat slats (3) and backrest slats (2)
  for (let i: number = 0; i < 3; i++) {
    const slat: Mesh = MeshBuilder.CreateBox(
      `${name}-seat-${i}`, { width: 2.6, height: 0.06, depth: 0.22 }, scene
    );
    slat.position = new Vector3(0, 0.48, -0.26 + i * 0.26);
    slat.material = mats.woodFixture;
    slat.parent = node;
  }
  for (let i: number = 0; i < 2; i++) {
    const slat: Mesh = MeshBuilder.CreateBox(
      `${name}-back-${i}`, { width: 2.6, height: 0.14, depth: 0.05 }, scene
    );
    slat.position = new Vector3(0, 0.78 + i * 0.22, 0.34);
    slat.rotation.x = -0.18;
    slat.material = mats.woodFixture;
    slat.parent = node;
  }
  // Curved steel legs (torus arcs read as bent tube)
  for (const side of [-1, 1]) {
    const leg: Mesh = MeshBuilder.CreateTorus(
      `${name}-leg-${side}`, { diameter: 0.46, thickness: 0.05, tessellation: 20 }, scene
    );
    leg.position = new Vector3(side * 1.15, 0.24, 0);
    leg.rotation.z = Math.PI / 2;
    leg.material = mats.metalFixture;
    leg.parent = node;
  }
  // Invisible collision block so players can't walk through the bench.
  const hull: Mesh = MeshBuilder.CreateBox(
    `${name}-hull`, { width: 2.7, height: 1.0, depth: 0.8 }, scene
  );
  hull.position = new Vector3(0, 0.5, 0.05);
  hull.isVisible = false;
  hull.checkCollisions = true;
  hull.parent = node;

  addContactShadow(scene, node, name, new Vector3(0, 0, 0.05), 1.6, 0.7);
}

/** Deterministic yaw from the prop's name so repeated plants don't all face
 *  the same way (the repetition is what reads as fake). */
function nameYaw(name: string): number {
  let hash: number = 0;
  for (let i: number = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return (hash % 360) * (Math.PI / 180);
}

/** Potted plant: a photoscanned CC0 plant when the prop library has it
 *  (instanced, so 40 of these cost one draw state), else the procedural
 *  pot + blob-canopy fallback. */
export function buildPlanterTree(
  scene: Scene,
  parent: TransformNode,
  name: string,
  pos: Vector3,
  scale: number = 1
): void {
  const lib: PropLibrary = PropLibrary.for(scene);
  if (lib.isAvailable("potted_plant_02")) {
    const node: TransformNode = new TransformNode(name, scene);
    node.parent = parent;
    node.position = pos.clone();
    lib.instantiate("potted_plant_02", node, new Vector3(0, 0, 0), {
      targetHeight: 1.5 * scale,
      rotationY: nameYaw(name),
    });
    // The scan's pot is ~35cm wide; the hull keeps players from clipping
    // through leaves without a per-leaf collider.
    const hull: Mesh = MeshBuilder.CreateCylinder(
      `${name}-hull`, { diameter: 0.8 * scale, height: 1.2 * scale, tessellation: 12 }, scene
    );
    hull.position = new Vector3(0, 0.6 * scale, 0);
    hull.isVisible = false;
    hull.checkCollisions = true;
    hull.parent = node;
    addContactShadow(scene, node, name, new Vector3(0, 0, 0), 0.7 * scale);
    return;
  }
  buildPlanterTreeProcedural(scene, parent, name, pos, scale);
}

/** Procedural fallback: tapered pot, rim, trunk, blobby canopy. */
export function buildPlanterTreeProcedural(
  scene: Scene,
  parent: TransformNode,
  name: string,
  pos: Vector3,
  scale: number = 1
): void {
  const mats = getSharedMaterials(scene);
  const node: TransformNode = new TransformNode(name, scene);
  node.parent = parent;
  node.position = pos.clone();
  node.scaling.setAll(scale);

  const pot: Mesh = MeshBuilder.CreateCylinder(
    `${name}-pot`, { diameterTop: 1.2, diameterBottom: 0.85, height: 0.8, tessellation: 24 }, scene
  );
  pot.position = new Vector3(0, 0.4, 0);
  pot.material = mats.planter;
  pot.parent = node;
  pot.checkCollisions = true;

  const rim: Mesh = MeshBuilder.CreateTorus(
    `${name}-rim`, { diameter: 1.18, thickness: 0.07, tessellation: 24 }, scene
  );
  rim.position = new Vector3(0, 0.8, 0);
  rim.material = mats.planter;
  rim.parent = node;

  const trunk: Mesh = MeshBuilder.CreateCylinder(
    `${name}-trunk`, { diameterTop: 0.1, diameterBottom: 0.16, height: 1.3, tessellation: 10 }, scene
  );
  trunk.position = new Vector3(0, 1.35, 0);
  trunk.material = mats.woodFixture;
  trunk.parent = node;

  // Canopy: three offset spheres so the silhouette is organic, not a ball.
  const blobs: Array<{ p: Vector3; d: number }> = [
    { p: new Vector3(0, 2.35, 0), d: 1.5 },
    { p: new Vector3(0.45, 2.05, 0.2), d: 1.0 },
    { p: new Vector3(-0.4, 2.15, -0.25), d: 1.1 },
  ];
  for (let i: number = 0; i < blobs.length; i++) {
    const blob: Mesh = MeshBuilder.CreateSphere(
      `${name}-canopy-${i}`, { diameter: blobs[i].d, segments: 10 }, scene
    );
    blob.position = blobs[i].p;
    blob.material = mats.foliage;
    blob.parent = node;
  }

  addContactShadow(scene, node, name, new Vector3(0, 0, 0), 0.85);
}

/** Round cafe table: disc top with rim, tapered pedestal, weighted base. */
export function buildCafeTable(
  scene: Scene,
  parent: TransformNode,
  name: string,
  pos: Vector3
): void {
  const mats = getSharedMaterials(scene);
  const node: TransformNode = new TransformNode(name, scene);
  node.parent = parent;
  node.position = pos.clone();

  const top: Mesh = MeshBuilder.CreateCylinder(
    `${name}-top`, { diameter: 1.5, height: 0.06, tessellation: 32 }, scene
  );
  top.position = new Vector3(0, 1.0, 0);
  top.material = mats.woodFixture;
  top.parent = node;

  const rim: Mesh = MeshBuilder.CreateTorus(
    `${name}-rim`, { diameter: 1.48, thickness: 0.05, tessellation: 32 }, scene
  );
  rim.position = new Vector3(0, 0.99, 0);
  rim.material = mats.metalFixture;
  rim.parent = node;

  const stem: Mesh = MeshBuilder.CreateCylinder(
    `${name}-stem`, { diameterTop: 0.09, diameterBottom: 0.14, height: 0.95, tessellation: 14 }, scene
  );
  stem.position = new Vector3(0, 0.5, 0);
  stem.material = mats.metalFixture;
  stem.parent = node;
  stem.checkCollisions = true;

  const base: Mesh = MeshBuilder.CreateCylinder(
    `${name}-base`, { diameterTop: 0.3, diameterBottom: 0.6, height: 0.06, tessellation: 24 }, scene
  );
  base.position = new Vector3(0, 0.03, 0);
  base.material = mats.metalFixture;
  base.parent = node;

  addContactShadow(scene, node, name, new Vector3(0, 0, 0), 0.8);
}

/** Round stool with a padded-looking top and tapered steel leg. */
export function buildStool(
  scene: Scene,
  parent: TransformNode,
  name: string,
  pos: Vector3
): void {
  const mats = getSharedMaterials(scene);
  const node: TransformNode = new TransformNode(name, scene);
  node.parent = parent;
  node.position = pos.clone();

  const seat: Mesh = MeshBuilder.CreateCylinder(
    `${name}-seat`, { diameter: 0.42, height: 0.1, tessellation: 20 }, scene
  );
  seat.position = new Vector3(0, 0.55, 0);
  seat.material = mats.woodFixture;
  seat.parent = node;

  const leg: Mesh = MeshBuilder.CreateCylinder(
    `${name}-leg`, { diameterTop: 0.07, diameterBottom: 0.11, height: 0.52, tessellation: 12 }, scene
  );
  leg.position = new Vector3(0, 0.27, 0);
  leg.material = mats.metalFixture;
  leg.parent = node;
  leg.checkCollisions = true;

  const foot: Mesh = MeshBuilder.CreateCylinder(
    `${name}-foot`, { diameterTop: 0.2, diameterBottom: 0.34, height: 0.04, tessellation: 16 }, scene
  );
  foot.position = new Vector3(0, 0.02, 0);
  foot.material = mats.metalFixture;
  foot.parent = node;

  addContactShadow(scene, node, name, new Vector3(0, 0, 0), 0.3);
}

/** Handle returned by buildHoloPedestal so a featured product can be
 *  dropped into the tube once catalog data arrives. */
export interface HoloPedestalHandle {
  /** Replace the placeholder core with a spinning featured-product card. */
  setFeatured(
    content: { imageUrl?: string; title: string; priceText: string },
    onPick: () => void
  ): void;
  /** Show (or update) the sold-count sign floating above the tube. */
  setSoldCount(unitsSold: number): void;
}

/** Holographic display pedestal: metal base, translucent glowing cylinder,
 *  and a slowly spinning emissive core inside — a wireframe polyhedron until
 *  setFeatured swaps in a product card. The spin runs on the scene render
 *  loop and glows under the GlowLayer. */
export function buildHoloPedestal(
  scene: Scene,
  parent: TransformNode,
  name: string,
  pos: Vector3,
  accentHex: string = "#6fb3e0"
): HoloPedestalHandle {
  const mats = getSharedMaterials(scene);
  const node: TransformNode = new TransformNode(name, scene);
  node.parent = parent;
  node.position = pos.clone();
  const accent: Color3 = Color3.FromHexString(accentHex);

  const base: Mesh = MeshBuilder.CreateCylinder(
    `${name}-base`, { diameterTop: 1.5, diameterBottom: 1.9, height: 0.5, tessellation: 32 }, scene
  );
  base.position = new Vector3(0, 0.25, 0);
  base.material = mats.metalFixture;
  base.parent = node;
  base.checkCollisions = true;
  addContactShadow(scene, node, name, new Vector3(0, 0, 0), 1.15);

  const ringMat: StandardMaterial = new StandardMaterial(`${name}-ring-mat`, scene);
  ringMat.diffuseColor = accent.scale(0.2);
  ringMat.emissiveColor = accent;
  ringMat.specularColor = new Color3(0, 0, 0);
  const ring: Mesh = MeshBuilder.CreateTorus(
    `${name}-ring`, { diameter: 1.55, thickness: 0.06, tessellation: 40 }, scene
  );
  ring.position = new Vector3(0, 0.52, 0);
  ring.material = ringMat;
  ring.parent = node;
  ring.isPickable = false;

  const tubeMat: StandardMaterial = new StandardMaterial(`${name}-tube-mat`, scene);
  tubeMat.diffuseColor = accent.scale(0.3);
  tubeMat.emissiveColor = accent.scale(0.35);
  tubeMat.alpha = 0.18;
  tubeMat.backFaceCulling = false;
  const tube: Mesh = MeshBuilder.CreateCylinder(
    `${name}-tube`, { diameter: 1.45, height: 2.4, tessellation: 32 }, scene
  );
  tube.position = new Vector3(0, 1.7, 0);
  tube.material = tubeMat;
  tube.parent = node;
  // Let clicks pass through the translucent tube to the featured card inside,
  // so players can open the product without standing inside the cylinder.
  tube.isPickable = false;

  const coreMat: StandardMaterial = new StandardMaterial(`${name}-core-mat`, scene);
  coreMat.diffuseColor = accent.scale(0.3);
  coreMat.emissiveColor = accent.scale(0.9);
  coreMat.wireframe = true;
  const core: Mesh = MeshBuilder.CreatePolyhedron(
    `${name}-core`, { type: 3, size: 0.42 }, scene
  );
  core.position = new Vector3(0, 1.7, 0);
  core.material = coreMat;
  core.parent = node;

  let soldSign: DynamicTextureSurface | null = null;
  let spinTarget: Mesh = core;
  const spinX: { value: number } = { value: 0.25 };
  scene.onBeforeRenderObservable.add(() => {
    const dt: number = scene.getEngine().getDeltaTime() / 1000;
    spinTarget.rotation.y += dt * 0.9;
    spinTarget.rotation.x += dt * spinX.value;
  });

  return {
    setFeatured(content, onPick): void {
      core.dispose();
      const card: DynamicTextureSurface = new DynamicTextureSurface({
        scene,
        name: `${name}-featured`,
        widthM: 1.15,
        heightM: 1.5,
        resolution: { width: 256, height: 334 },
        theme: {
          background: "#15181c",
          foreground: "#f4f6f8",
          accent: accentHex,
          fontFamily: "sans-serif",
          fontFamilySerif: "serif",
        },
        emissive: true,
        layout: "imageTop",
        excludeFromGlow: true, // photo whites must not bloom
      });
      card.placeAt({ position: new Vector3(0, 1.7, 0), rotationY: 0, parent: node });
      card.setContent({
        title: content.title,
        imageUrl: content.imageUrl,
        bodyLines: [content.priceText],
        ctaText: "VIEW",
      });
      card.onPick(onPick);
      spinTarget = card.mesh;
      spinX.value = 0; // product cards spin upright, no tumble
    },
    setSoldCount(unitsSold: number): void {
      if (soldSign === null) {
        soldSign = new DynamicTextureSurface({
          scene,
          name: `${name}-sold-sign`,
          widthM: 1.5,
          heightM: 0.5,
          resolution: { width: 384, height: 128 },
          theme: {
            background: "#15181c",
            foreground: "#f4f6f8",
            accent: accentHex,
            fontFamily: "sans-serif",
            fontFamilySerif: "serif",
          },
          emissive: true,
          layout: "wayfinding",
        });
        // Readable from spawn/fountain south of the pedestals (rotY π faces -z).
        soldSign.placeAt({ position: new Vector3(0, 3.35, 0), rotationY: Math.PI, parent: node });
      }
      soldSign.setContent({ title: `${unitsSold} SOLD` });
    },
  };
}

/** Modern glass railing: steel posts, round handrail tube, glass infill.
 *  Length runs along local +x; rotate via rotY. A single invisible box is
 *  the collider. */
export function buildGlassRailing(
  scene: Scene,
  parent: TransformNode,
  name: string,
  center: Vector3,
  length: number,
  rotY: number
): void {
  const mats = getSharedMaterials(scene);
  const node: TransformNode = new TransformNode(name, scene);
  node.parent = parent;
  node.position = center.clone();
  node.rotation.y = rotY;

  const postCount: number = Math.max(2, Math.round(length / 1.8) + 1);
  for (let i: number = 0; i < postCount; i++) {
    const x: number = -length / 2 + (length * i) / (postCount - 1);
    const post: Mesh = MeshBuilder.CreateCylinder(
      `${name}-post-${i}`, { diameter: 0.07, height: 1.05, tessellation: 10 }, scene
    );
    post.position = new Vector3(x, 0.525, 0);
    post.material = mats.metalFixture;
    post.parent = node;
  }

  const rail: Mesh = MeshBuilder.CreateCylinder(
    `${name}-rail`, { diameter: 0.09, height: length, tessellation: 12 }, scene
  );
  rail.rotation.z = Math.PI / 2;
  rail.position = new Vector3(0, 1.08, 0);
  rail.material = mats.metalFixture;
  rail.parent = node;

  const glass: Mesh = MeshBuilder.CreateBox(
    `${name}-glass`, { width: length, height: 0.85, depth: 0.03 }, scene
  );
  glass.position = new Vector3(0, 0.55, 0);
  glass.material = mats.glass;
  glass.parent = node;

  const hull: Mesh = MeshBuilder.CreateBox(
    `${name}-hull`, { width: length, height: 1.15, depth: 0.12 }, scene
  );
  hull.position = new Vector3(0, 0.575, 0);
  hull.isVisible = false;
  hull.checkCollisions = true;
  hull.parent = node;
}
